// packages/api/src/hooks/useWebPush.ts
//
// Web Push (browser push transport) — RFC 8030/8291/8292 sobre as notificações
// in-app existentes. Contrato: docs/contracts/ESCR-web-push-transport.md.
//
// Endpoints (JwtAuthGuard, self-escopados em currentUser.id):
//   GET    /notifications/vapid-public-key → { publicKey } ('' quando off)
//   POST   /notifications/subscriptions    → WebPushSubscription
//   DELETE /notifications/subscriptions    → { removed }

import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  ApiSuccess,
  CreatePushSubscriptionInput,
  DeletePushSubscriptionInput,
  VapidPublicKeyResponse,
  WebPushSubscription,
} from '@alamo/types';

import { apiClient } from '../client';
import { webPushKeys } from '../query-keys';

/**
 * Converte a chave pública VAPID (base64url) no `Uint8Array` esperado por
 * `pushManager.subscribe({ applicationServerKey })`. O padding e a troca de
 * caracteres base64url→base64 são exigidos pela Push API do navegador.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Busca a chave pública VAPID para o browser (applicationServerKey).
 * `publicKey === ''` ⇒ Web Push desabilitado no backend → não tentar inscrever.
 */
export function useVapidPublicKey(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: webPushKeys.vapidPublicKey(),
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<string> => {
      const res = await apiClient.get<ApiSuccess<VapidPublicKeyResponse>>(
        '/notifications/vapid-public-key',
      );
      return res.data.data.publicKey;
    },
  });
}

/** Registra (upsert por endpoint) a inscrição PushManager do navegador. */
export function useSubscribeWebPush() {
  return useMutation({
    mutationFn: async (input: CreatePushSubscriptionInput): Promise<WebPushSubscription> => {
      const res = await apiClient.post<ApiSuccess<WebPushSubscription>>(
        '/notifications/subscriptions',
        input,
      );
      return res.data.data;
    },
  });
}

/** Remove a inscrição do usuário atual identificada por endpoint (idempotente). */
export function useUnsubscribeWebPush() {
  return useMutation({
    mutationFn: async (input: DeletePushSubscriptionInput): Promise<{ removed: number }> => {
      const res = await apiClient.delete<ApiSuccess<{ removed: number }>>(
        '/notifications/subscriptions',
        { data: input },
      );
      return res.data.data;
    },
  });
}
